import React, { useState, useEffect, useImperativeHandle } from 'react';


import { useSelector } from 'react-redux';
import {
  Modal, Box, Typography, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Chip
} from '@mui/material';
import NavButton from './ui/NavButton';
import api from '../functions/api';
import StockChart from './charts/Stockchart_MUI';
import SearchBox2 from '../functions/Search_Generic';

const Charts = React.forwardRef(({ disabled }, ref) => {
  const vdpData = useSelector(state => state.portfolio.parametersData?.VDP_dataset_json || []);
  const [open, setOpen] = useState(false);
  const [selections, setSelections] = useState([]); // {type: 'sentiment'|'ticker', id: string}
  const [dataMap, setDataMap] = useState({}); // key: label, value: series
  const [loading, setLoading] = useState(false);
  const [normalize, setNormalize] = useState(false); // false = raw, true = index=100
  const [benchmarkList, setBenchmarkList] = useState([]);
  const [startDate, setStartDate] = useState(() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split("T")[0]; // format: YYYY-MM-DD
});


  useEffect(() => {
  if (open) {
    api.get('/get_benchmark_list').then(res => {
      const rows = res.data?.data || [];
      console.log("📊 Raw benchmark rows from API:", rows); // ✅ Log raw data

      const formatted = rows.map(row => ({
  id: row[1],      // ✅ ticker: ^IXIC, ^GSPC, etc.
  label: row[2]    // ✅ readable name: Nasdaq, S&P500, etc.
}));


      console.log("✅ Parsed benchmarkList:", formatted); // ✅ Log parsed list

      setBenchmarkList(formatted);
    });
  }
}, [open]);

useEffect(() => {
  // Refetch on startDate change
  selections.forEach(fetchAndStore);
}, [startDate]);


  useEffect(() => {
  // Refetch all current selections when display mode changes
  selections.forEach(fetchAndStore);
}, [normalize]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelections([]);
    setDataMap({});
    setLoading(false);
  };

  // Expose showModal function to parent component via ref
  useImperativeHandle(ref, () => ({
    showModal: handleOpen
  }));

  const fetchAndStore = async (entry) => {
  setLoading(true);
  try {
    let transformed = [];

    if (entry.type === 'sentiment') {
      const res = await api.get('/sentiment_hist_data', {
        params: { sentiment: entry.id }
      });

      transformed = res.data.map(d => {
        const val = d.value ?? d.Close;
        const num = Number(val);
        return {
          date: new Date(d.epoch * 1000).toISOString().split("T")[0],
          value: isNaN(num) ? null : num
        };
      });

    } else {
      // ticker or benchmark, same logic
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);

      const payload = {
        ticker: entry.id,
        date_start: oneYearAgo.toISOString().split("T")[0],
        date_end: today.toISOString().split("T")[0]
      };

      const res = await api.put('/historical_data', payload);
      const records = res.data?.data ?? [];

      transformed = records.map(row => ({
        date: row.date,
        value: typeof row.close === 'number' ? row.close : null
      }));
    }

    // ✅ Filter by startDate first
    transformed = transformed.filter(d => new Date(d.date) >= new Date(startDate));

    // ✅ Normalize after filtering
    if (normalize && transformed.length > 0) {
      const base = transformed[0]?.value;
      if (typeof base === 'number' && base !== 0) {
        transformed = transformed.map(d => ({
          ...d,
          value: d.value !== null ? (d.value / base) * 100 : null
        }));
      }
    }

    const label = normalize ? `${entry.label} (Index=100)` : entry.label;

    setDataMap(prev => {
      const cleaned = Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(entry.label))
      );
      return {
        ...cleaned,
        [label]: transformed
      };
    });

  } catch (err) {
    console.error("Data fetch error", err);
  } finally {
    setLoading(false);
  }
};




  const handleAddSelection = (entry) => {
    const currentCount = selections.length;
    if (currentCount >= 2) return;
    const alreadyExists = selections.some(s => s.id === entry.id && s.type === entry.type);
    if (!alreadyExists) {
      const newSelections = [...selections, entry];
      setSelections(newSelections);
      fetchAndStore(entry);
    }
  };

  const handleSentimentSelect = (e) => {
    const val = e.target.value;
    const found = vdpData.find(item => item.Vector_datafield === val);
    if (found) {
      handleAddSelection({ type: 'sentiment', id: found.Vector_datafield, label: found.Label });
    }
  };

  const handleTickerSelect = (ticker) => {
    handleAddSelection({ type: 'ticker', id: ticker, label: ticker });
  };

  const handleRemove = (id) => {
  setSelections(prev => {
    const updated = prev.filter(sel => sel.id !== id);
    const removed = prev.find(sel => sel.id === id);
    if (removed) {
      setDataMap(prevMap => {
        const newMap = Object.fromEntries(
          Object.entries(prevMap).filter(([key]) =>
            !key.startsWith(removed.label)
          )
        );
        return newMap;
      });
    }
    return updated;
  });
};



  const mergedSeries = Object.entries(dataMap).map(([label, data]) => ({
  label,
  data: data.filter(d => new Date(d.date) >= new Date(startDate))
}));



const chartTitle = (() => {
  const labels = selections.map(s => s.label);
  const mode = normalize ? " (Indexed)" : " (Raw)";
  return labels.length > 0 ? labels.join(" vs ") + mode : "Chart";
})();


  return (
    <>
      <NavButton label="Charts" onClick={handleOpen} disabled={disabled} />
      <Modal open={open} onClose={handleClose} disableEnforceFocus>
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700, maxHeight: '90vh',
            bgcolor: 'background.paper', borderRadius: 2,
            boxShadow: 24, p: 4, overflowY: 'auto'
          }}
        >
          <Typography variant="h6" mb={2}>Select up to 2 Sentiments or Tickers</Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="select-sentiment">Indicator</InputLabel>
            <Select
              labelId="select-sentiment"
              value=""
              label="Indicator"
              onChange={handleSentimentSelect}
            >
              {vdpData.map(item => (
                <MenuItem key={item.ID} value={item.Vector_datafield}>
                  {item.Label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <SearchBox2 onSelectTicker={handleTickerSelect} />

          <FormControl fullWidth sx={{ mb: 2 }}>
  <InputLabel id="select-benchmark">Benchmark</InputLabel>
  <Select
    labelId="select-benchmark"
    value=""
    label="Benchmark"
    onChange={(e) => {
      const selected = benchmarkList.find(b => b.id === e.target.value);
      if (selected) {
        handleAddSelection({ type: 'benchmark', id: selected.id, label: selected.label });
      }
    }}
    
  >
    {benchmarkList.map(b => (
      <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
    ))}
  </Select>
</FormControl>


          {selections.length > 0 && (
            <Box mt={2}>
              {selections.map(sel => (
                <Chip
                  key={sel.id}
                  label={`${sel.type === 'sentiment' ? '🧠' : '📈'} ${sel.label}`}
                  onDelete={() => handleRemove(sel.id)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
  <InputLabel id="mode-label">Display Mode</InputLabel>
  <Select
    labelId="mode-label"
    value={normalize ? 'indexed' : 'raw'}
    label="Display Mode"
    onChange={(e) => setNormalize(e.target.value === 'indexed')}
  >
    <MenuItem value="raw">Raw Values</MenuItem>
    <MenuItem value="indexed">Index 100 at Start</MenuItem>
  </Select>
</FormControl>

<FormControl fullWidth sx={{ mb: 2 }}>
  <InputLabel shrink htmlFor="start-date">Start Date</InputLabel>
  <input
    id="start-date"
    type="date"
    value={startDate}
    onChange={e => setStartDate(e.target.value)}
    style={{ width: '100%', padding: '8px', fontSize: '1rem' }}
  />
</FormControl>


          {loading && <CircularProgress sx={{ mt: 3 }} />}

          {!loading && mergedSeries.length > 0 && (
            <Box mt={3}>
              <StockChart
  selectedPeriod="1Y"
  title={chartTitle}
  seriesData={mergedSeries[0].data}
  seriesKey="value"
  seriesName={mergedSeries[0].label}
  tooltipFields={[
    { source: "seriesData", key: "value", label: mergedSeries[0].label, format: "number" },
    mergedSeries[1] && {
      source: "comparison",
      key: "value",
      label: mergedSeries[1].label,
      format: "number"
    }
  ].filter(Boolean)}
  comparisonSeries={
  mergedSeries[1]
    ? mergedSeries[1].data.map(d => ({
        ...d,
        comparison_value: d.value
      }))
    : null
}
comparisonKey="comparison_value"

  comparisonLabel={mergedSeries[1]?.label || ""}
  yAxisFormat="number"
  showToolbar={false}
  height={300}
/>

            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
});

export default Charts;
